import React, { useState } from 'react';
import './FIIFaq.css';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQ_DATA: FAQItem[] = [
  {
    id: 'irrf-dedo-duro',
    question: 'O que é o IRRF retido na fonte ("dedo-duro") em vendas de FII?',
    answer: `Em toda venda de cotas de FII, a corretora retém na fonte 0,005% do valor bruto da operação. Esse valor é chamado popularmente de "dedo-duro" porque sua função principal é informar à Receita Federal que houve uma alienação — não tem caráter arrecadatório significativo.

**Como o sistema usa o IRRF**

- Ao registrar uma venda, o campo "IRRF Retido" é preenchido automaticamente com 0,005% do valor da venda. Você pode editá-lo para refletir o valor exato da nota de corretagem (corretoras arredondam de formas diferentes).
- No Controle DARF, o IRRF retido no mês é deduzido do imposto apurado (20% sobre o lucro). A DARF a pagar é o valor líquido após essa compensação.
- Se o IRRF retido for maior que o imposto do mês (ou se o mês foi de prejuízo), o saldo não usado acumula automaticamente para abater DARFs dos meses seguintes.
- O saldo de IRRF não compensado em cada ano-calendário aparece na seção "Resumo Anual para Declaração (DIRPF)" — esse valor pode ser informado na declaração anual como imposto pago/retido para fins de restituição.

**Onde declarar na DIRPF**

- Os ganhos de capital tributados (após a compensação) vão na ficha "Renda Variável → Operações com Fundos Imobiliários".
- O total de IRRF retido no ano vai como imposto pago na mesma ficha. Use os valores da coluna "IRRF Retido" do resumo anual.`,
  },
  {
    id: 'darf-atraso',
    question: 'Pagamento de Diferença de DARF em Atraso',
    answer: `Sim, existe forma de regularizar isso. Você precisa emitir uma nova DARF complementar pelo valor da diferença com os acréscimos legais.

**Como calcular o valor a pagar**

O valor devido é a diferença de R$ 24,00, acrescida de:
1. Multa de mora: 0,33% ao dia, limitada a 20% — como já ultrapassou muito tempo, a multa está no teto de 20%, ou seja, R$ 4,80.
2. Juros Selic: acumulados desde outubro/2025 (mês seguinte ao vencimento) até o mês do pagamento. Você precisa consultar a taxa Selic acumulada no período no site do Banco Central ou da Receita Federal para calcular o valor exato dos juros sobre os R$ 24,00.
O vencimento original da DARF de 09/2025 seria último dia útil de outubro/2025.

**Como emitir a DARF complementar**

Acesse o Sicalc (Sistema de Cálculo de Acréscimos Legais) da Receita Federal, disponível em: https://sicalc.receita.fazenda.gov.br
Preencha assim:

- Código da receita: 6015 (ganho de capital em renda variável — FII)
- Período de apuração: 09/2025
- Data de vencimento: 31/10/2025
- Valor principal: R$ 24,00

O próprio sistema calculará automaticamente a multa e os juros Selic, gerando o DARF já com o valor atualizado para pagamento.

**Pontos importantes**

O valor final provavelmente ficará em torno de R$ 30 a R$ 32, mas o Sicalc te dará o número exato. Como o valor principal da diferença é inferior a R$ 10,00, normalmente não seria possível emitir DARF — mas como você está pagando a diferença de um imposto que já teve parte recolhida, o valor de R$ 24,00 está acima do mínimo e pode ser emitido normalmente.
Após o pagamento, guarde o comprovante junto com a DARF original para fins de controle e eventual comprovação perante a Receita.`,
  },
];

function linkify(text: string): React.ReactNode {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    part.match(urlRegex) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
    ) : (
      part
    )
  );
}

function renderAnswer(text: string) {
  const blocks = text.split(/(?=\*\*)/);
  const result: React.ReactNode[] = [];
  blocks.forEach((block, i) => {
    const headerMatch = block.match(/^\*\*(.+?)\*\*\s*/);
    if (headerMatch) {
      result.push(<h4 key={`h-${i}`}>{headerMatch[1]}</h4>);
      block = block.slice(headerMatch[0].length);
    }
    const lines = block.split('\n').filter(Boolean);
    lines.forEach((line, j) => {
      if (line.startsWith('- ')) {
        result.push(<p key={`${i}-${j}`} className="faq-bullet">{linkify(line.slice(2))}</p>);
      } else {
        result.push(<p key={`${i}-${j}`}>{linkify(line)}</p>);
      }
    });
  });
  return result;
}

const FIIFaq: React.FC = () => {
  const [expandedId, setExpandedId] = useState<string | null>(FAQ_DATA[0]?.id ?? null);

  return (
    <div className="fii-faq">
      <div className="page-header">
        <div>
          <h1>❓ FAQ FIIs</h1>
          <p>Perguntas frequentes e úteis sobre Fundos Imobiliários</p>
        </div>
      </div>

      <div className="faq-list">
        {FAQ_DATA.map((item) => (
          <div
            key={item.id}
            className={`faq-item ${expandedId === item.id ? 'expanded' : ''}`}
          >
            <button
              className="faq-question"
              onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              aria-expanded={expandedId === item.id}
            >
              <span>{item.question}</span>
              <span className="faq-chevron">▼</span>
            </button>
            {expandedId === item.id && (
              <div className="faq-answer">
                {renderAnswer(item.answer)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FIIFaq;
